import { useState } from "react";
import { FileDown, Loader2 } from "lucide-react";

// =============================================================================
// QuotePdfButton — the quote's PDF, right where the quote is mentioned
// =============================================================================
// James, 6 Oct 2026, on the project's Financials tab: "I want the quote from a
// project to be clickable from Finance in projects, not just a go-to-quote
// link then an extract PDF — the sent copy should be just there in projects,
// ready to download and see, not nested in Finance. Same as variations."
//
// The sent copy IS stored: every "Export PDF" on a quote writes the file to
// storage quote-pdfs/{quote_id}/{n}.pdf and stamps quotes.pdf_storage_path
// (quotePdf.ts). So this button opens that stored copy in a new tab. If a
// quote has never had its PDF made (pdf_storage_path null), the button makes
// it the same way the quote screen does, stores it, then opens it — and the
// label says which it did. pdfmake loads on click, never in the main bundle.
// =============================================================================

export default function QuotePdfButton({ quoteId, hasPdf, preparedBy, small }: {
  quoteId: string;
  hasPdf: boolean;
  preparedBy: { name: string; email: string };
  small?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function open(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const mod = await import("@/features/quotes/quotePdf");
      let url = hasPdf ? await mod.getLatestQuotePdfUrl(quoteId) : null;
      if (!url) {
        const r = await mod.generateQuotePdf(quoteId, preparedBy);
        url = r.signedUrl;
      }
      window.open(url, "_blank", "noopener");
    } catch (ex: any) {
      setErr(ex?.message ?? "Could not open the PDF.");
    } finally {
      setBusy(false);
    }
  }

  const cls = small
    ? "inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-text-muted hover:text-accent hover:border-accent disabled:opacity-50"
    : "inline-flex items-center gap-1.5 rounded-md border border-line bg-ink-card px-2.5 py-1.5 text-[12px] font-medium text-text hover:border-accent hover:text-accent disabled:opacity-50";
  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" onClick={open} disabled={busy} className={cls}
        title={hasPdf ? "Open the sent PDF" : "No PDF made yet — make it now, then open"}>
        {busy ? <Loader2 size={small ? 10 : 13} className="animate-spin"/> : <FileDown size={small ? 10 : 13}/>}
        {hasPdf ? "PDF" : "Make PDF"}
      </button>
      {err && <span className="text-[11px] text-danger">{err}</span>}
    </span>
  );
}
